// @ts-nocheck

/* eslint-disable sonarjs/no-duplicate-string */
import * as Sentry from '@sentry/react';
import { reduce as _reduce, each as _each, merge as _merge, size as _size, isEqual } from 'lodash';

import Client from '../client';
import { Target } from '../client';
import config from '../config/app.config';
import { RedirectServices, getRedirectUrl } from '../config/urls';
import { __, getLang } from '../i18n/l10n';
import { basketResetBoomActives } from '../modules/boom/actions/basket';
import { getBoomErrorLabelsByErrorCode } from '../modules/boom/constants/errorLabels';
import { selectBoomGlobalSkinId } from '../modules/boom/selectors/global/globalPrimary.selector';
import { basketItemSetIsGRActive, basketSetIsGRActive, resetAllGRActives } from '../modules/gift-race/actions/basket';
import { getGRErrorLabelsByErrorCode } from '../modules/gift-race/constants/errorLabels';
import { selectGRGlobalSkinId } from '../modules/gift-race/selectors/global/globalPrimary.selector';
import {
  basketItemSetKLEligibleCollectible,
  basketSetKLEligibleCollectible,
  resetKLEligibleCollectibles,
} from '../modules/koh-lanta/actions/basket';
import { KLErrorLabelsByErrorCode } from '../modules/koh-lanta/constants/labels';
import { basketItemSetMPCard, basketSetMPCard, resetAllMPActives } from '../modules/monopoly/actions/basket';
import { getMPErrorLabelsByErrorCode } from '../modules/monopoly/constants/labels';
import { type MMAPIResponseResult } from '../states/basket';
import BetBuilder from '../states/betbuilder';
import { BasketActionTypes } from '../types/actions/basket';
import { BasketModes, BasketStatuses, BetslipErrorCodes } from '../types/basket';
import { basket as BasketUtils, getCustomHeaders } from '../utils';

import apiActionCreators from './api';
import entitiesActionCreators from './entities';
import { externalLinkOpen } from './externallink';
import reportActionsCreators from './report';
import storageActionCreators from './storage';

const { basketUrl } = config;
const { receiveEntities } = entitiesActionCreators;
const { storageBasketSave } = storageActionCreators;
const { makeApiCall } = apiActionCreators;
const { sendReport } = reportActionsCreators;

const createRuleFunction = (rule) => {
  let ruleFunction = undefined;
  try {
    // eslint-disable-next-line no-new-func
    ruleFunction = new Function('betslip', rule);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.group('freebet/booster rule function error');
    // eslint-disable-next-line no-console
    console.error(`error: ${error}`);
    // eslint-disable-next-line no-console
    console.log(`function: ${rule}`);
    // eslint-disable-next-line no-console
    console.groupEnd('rule function error');
  }
  return ruleFunction;
};

const basketSetBooster = (eligibleBooster) => ({
  type: BasketActionTypes.SET_ELIGIBLE_BOOSTER,
  payload: {
    eligibleBooster,
  },
});

export const basketSanitize = () => ({
  type: BasketActionTypes.SANITIZE,
});

/**
 * Create an action to set basket's eligible freebets
 *
 * @param  {Array}  options.freebetIds The free bet id list
 * @return {Object}                    Return an action object
 */
const basketSetFreebets = ({ eligibleFreebets }) => ({
  type: BasketActionTypes.SET_ELIGIBLE_FREEBETS,
  payload: {
    eligibleFreebets,
  },
});

const basketSetBonuses = ({ eligibleBonuses }) => ({
  type: BasketActionTypes.SET_ELIGIBLE_BONUSES,
  payload: {
    eligibleBonuses,
  },
});

/**
 * Create an action to set the outcome's eligible free bets
 *
 * @param  {number} options.oddId           The outcome id
 * @param  {Array} options.eligibleFreebets The free bet id list
 * @return {Object}                         Return an action object
 */
const basketSetItemFreebets = ({ oddId, eligibleFreebets }) => ({
  type: BasketActionTypes.SET_ITEM_ELIGIBLE_FREEBETS,
  payload: {
    oddId,
    eligibleFreebets,
  },
});

const basketSetItemBonuses = ({ oddId, eligibleBonuses }) => ({
  type: BasketActionTypes.SET_ITEM_ELIGIBLE_BONUSES,
  payload: {
    oddId,
    eligibleBonuses,
  },
});

/**
 * Dispatches an action to update the eligible freebets when we use the freebets switch
 *
 * @return {function}  The function dispatcher that send the action
 */
export const basketCheckEligibleFreebets = () => (dispatch, getState) => {
  const { basket, entities } = getState();
  const { items, freebets } = basket;

  const oddIdByFreebetIds = getOddIdByFreebetIds(freebets, items, entities);
  const eligibleFreebetsByOddIds = getEligibleFreebetByOddsIds(oddIdByFreebetIds);

  /* Remove inexistant freebet id */
  _each(items, ({ oddId }) => {
    if (eligibleFreebetsByOddIds[oddId] !== undefined) {
      const eligibleFreebets = eligibleFreebetsByOddIds[oddId];

      dispatch(basketSetItemFreebets({ oddId, eligibleFreebets }));
    } else {
      dispatch(basketSetItemFreebets({ oddId }));
    }
  });
};

/**
 * Construct a new format of eligible freebets by using the basket freebets data
 *
 * @param {Object} freebets the basket freebets
 * @param {Object} items the basket items
 * @param {Object} entities the basket entities
 * @return {Object}  a formatted object containing all the `isUsed`, an array of relative `oddIds` and `freebetId`
 */
const getOddIdByFreebetIds = (freebets, items, entities) => {
  return _reduce(
    freebets,
    (finalOddIdByFreebetIds, { freebetId, rule, deleted }) => {
      if (!deleted) {
        const ruleFunction = createRuleFunction(rule);
        if (ruleFunction !== undefined) {
          const oddIdByFreebetId = _reduce(
            items,
            (finalOddIdByFreebetId, item) => {
              const { matchId, betId, oddId, selectedFreebetId, betbuilder, value } = item;
              const match = entities.matches[matchId];
              const bet = entities.bets[betId];
              const odd = entities.odds[oddId];
              const outcome = entities.outcomes[oddId];

              /* MyMatch case */
              if (betbuilder && !!match) {
                let isOk = false;
                const subOutcomes = BetBuilder.aggregateRelatedBetsAndOutcomes(
                  item.selectionItems,
                  entities.bets,
                  entities.outcomes,
                );
                try {
                  isOk = ruleFunction([
                    {
                      match,
                      bet: { betTypeId: 2335 },
                      outcome: { odd: value },
                      subOutcomes,
                    },
                  ]);
                } catch (error) {
                  // eslint-disable-next-line no-console
                  console.group('freebet/booster rule function error');
                  // eslint-disable-next-line no-console
                  console.error(`error: ${error}`);
                  // eslint-disable-next-line no-console
                  console.log(`function: ${rule}`);
                  // eslint-disable-next-line no-console
                  console.groupEnd('rule function error');
                }
                if (isOk === true) {
                  return {
                    ...finalOddIdByFreebetId,
                    [freebetId]: {
                      ...finalOddIdByFreebetId[freebetId],
                      freebetId,
                      isUsed: finalOddIdByFreebetId[freebetId].isUsed === true || selectedFreebetId === freebetId,
                      oddIds: [...finalOddIdByFreebetId[freebetId].oddIds, oddId],
                    },
                  };
                }
                /* default case */
              } else if (!!match && !!bet && !!outcome && odd !== undefined && odd !== null) {
                const finalOutcome = {
                  ...outcome,
                  odd,
                };
                let isOk = false;
                try {
                  isOk = ruleFunction([{ match, bet, outcome: finalOutcome }]);
                } catch (error) {
                  // eslint-disable-next-line no-console
                  console.group('freebet/booster rule function error');
                  // eslint-disable-next-line no-console
                  console.error(`error: ${error}`);
                  // eslint-disable-next-line no-console
                  console.log(`function: ${rule}`);
                  // eslint-disable-next-line no-console
                  console.groupEnd('rule function error');
                }
                if (isOk === true) {
                  return {
                    ...finalOddIdByFreebetId,
                    [freebetId]: {
                      ...finalOddIdByFreebetId[freebetId],
                      freebetId,
                      isUsed: finalOddIdByFreebetId[freebetId].isUsed === true || selectedFreebetId === freebetId,
                      oddIds: [...finalOddIdByFreebetId[freebetId].oddIds, oddId],
                    },
                  };
                }
              }
              return finalOddIdByFreebetId;
            },
            { [freebetId]: { oddIds: [], isUsed: false } },
          );
          return { ...finalOddIdByFreebetIds, ...oddIdByFreebetId };
        }
      }
      return finalOddIdByFreebetIds;
    },
    {},
  );
};

/**
 * Construct a new object containing the eligible freebets based on each odd id
 *
 * @param {Object} oddIdByFreebetIds the temporary formatted object
 *
 * @return {Object} a formatted object containing all the `isUsed`, an array of relative `oddIds` and `freebetId`
 */
const getEligibleFreebetByOddsIds = (oddIdByFreebetIds) => {
  return _reduce(
    oddIdByFreebetIds,
    (finalEligibleFreebetsByOddIds, { freebetId, oddIds, isUsed }) => {
      const eligibleFreebetsByOddId = _reduce(
        oddIds,
        (finalEligibleFreebetsByOddId, oddId) => ({
          ...finalEligibleFreebetsByOddId,
          [oddId]: {
            ...finalEligibleFreebetsByOddId[oddId],
            [freebetId]: {
              freebetId,
              isUsed,
            },
          },
        }),
        {},
      );
      return _merge({}, finalEligibleFreebetsByOddIds, eligibleFreebetsByOddId);
    },
    {},
  );
};

/**
 * Add some free bets to basket and outcomes according to
 * the rule of each free bet
 *
 * @return {function}   The function that add some free bets
 */
export const basketCheckFreebets = () => (dispatch, getState) => {
  // Check freebets
  let eligibleFreebetsGlobal = [];

  const { basket, entities } = getState();
  const { items, freebets } = basket;

  /* Check for MODE SIMPLE */
  const oddIdByFreebetIds = getOddIdByFreebetIds(freebets, items, entities);
  const eligibleFreebetsByOddIds = getEligibleFreebetByOddsIds(oddIdByFreebetIds);

  /* Check for MODE COMBINED */
  _each(freebets, ({ freebetId, rule, deleted }) => {
    if (!deleted) {
      const ruleFunction = createRuleFunction(rule);
      if (ruleFunction !== undefined) {
        const tmpItems = [];
        _each(items, ({ matchId, betId, oddId, betbuilder, selectionItems, value }) => {
          const match = entities.matches[matchId];
          const bet = entities.bets[betId];
          const odd = entities.odds[oddId];
          const outcome = entities.outcomes[oddId];

          if (betbuilder && !!match) {
            const subOutcomes = BetBuilder.aggregateRelatedBetsAndOutcomes(
              selectionItems,
              entities.bets,
              entities.outcomes,
            );
            tmpItems.push({
              match,
              bet: { betTypeId: 2335 },
              outcome: { odd: value },
              subOutcomes,
            });
          } else if (!!match && !!bet && !!outcome && odd !== undefined && odd !== null) {
            const finalOutcome = {
              ...outcome,
              odd,
            };
            tmpItems.push({ match, bet, outcome: finalOutcome });
          }
        });
        let isOk = false;
        try {
          isOk = ruleFunction(tmpItems);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.group('freebet/booster rule function error');
          // eslint-disable-next-line no-console
          console.error(`error: ${error}`);
          // eslint-disable-next-line no-console
          console.log(`function: ${rule}`);
          // eslint-disable-next-line no-console
          console.groupEnd('rule function error');
        }
        if (tmpItems.length > 0 && isOk === true) {
          eligibleFreebetsGlobal.push(freebetId);
        }
      }
    }
  });

  /* Remove inexistant freebet id */
  _each(items, ({ eligibleFreebets: currentEligibleFreebets, oddId }) => {
    if (isEqual(currentEligibleFreebets, eligibleFreebetsByOddIds[oddId])) {
      return;
    }

    if (eligibleFreebetsByOddIds[oddId] !== undefined) {
      const eligibleFreebets = eligibleFreebetsByOddIds[oddId];
      dispatch(basketSetItemFreebets({ oddId, eligibleFreebets }));
    } else {
      dispatch(basketSetItemFreebets({ oddId }));
    }
  });

  if (basket.selectedFreebetId !== undefined) {
    if (eligibleFreebetsGlobal.indexOf(basket.selectedFreebetId) < 0) {
      dispatch({
        type: BasketActionTypes.SELECTED_FREEBET_CHANGE,
        payload: {},
      });
    }
  }
  if (eligibleFreebetsGlobal.length === 0) {
    eligibleFreebetsGlobal = undefined;
  }
  if (!isEqual(eligibleFreebetsGlobal, basket.eligibleFreebets)) {
    dispatch(basketSetFreebets({ eligibleFreebets: eligibleFreebetsGlobal }));
  }
};

/**
 * Set the outcome's selected free bet and dispatch an action to
 * update the free bets
 *
 * @param  {number} options.oddId     The outcome id
 * @param  {number} options.freebetId The free bet id
 * @return {function}                 Return a function
 */
export const basketItemSelectedFreebetChange =
  ({ oddId, freebetId }) =>
  (dispatch) => {
    dispatch({
      type: BasketActionTypes.ITEM_SELECTED_FREEBET_CHANGE,
      payload: {
        oddId,
        freebetId,
      },
    });
    dispatch(basketCheckFreebets());
  };

export const basketItemSelectedBonusChange =
  ({ oddId, promotionId }) =>
  (dispatch) => {
    dispatch({
      type: BasketActionTypes.ITEM_SELECTED_BONUS_CHANGE,
      payload: {
        oddId,
        promotionId,
      },
    });
  };

export const basketSelectedFreebetChange =
  ({ freebetId }) =>
  (dispatch) => {
    dispatch({
      type: BasketActionTypes.SELECTED_FREEBET_CHANGE,
      payload: { freebetId },
    });
    dispatch(basketCheckFreebets());
  };

export const basketSelectedBonusChange =
  ({ promotionId }) =>
  (dispatch) => {
    dispatch({
      type: BasketActionTypes.SELECTED_BONUS_CHANGE,
      payload: { promotionId },
    });
  };

export const basketAddItem = ({
  oddId,
  matchId,
  betId,
  sourceElementUuid,
  isFromLineUp,
  isFromMultichoice,
  isRepeatBet,
  isFromHistory,
}) => ({
  type: BasketActionTypes.ADD_ITEM,
  payload: {
    oddId,
    matchId,
    betId,
    ts: Date.now(),
    sourceElementUuid,
    isFromLineUp,
    isFromMultichoice,
    isRepeatBet,
    isFromHistory,
  },
});

export const basketAddItems = ({ bets, betslipReference, sourceElementUuid, isRepeatBet, isFromHistory = false }) => ({
  type: BasketActionTypes.ADD_ITEMS,
  payload: {
    bets,
    betslipReference,
    sourceElementUuid,
    isRepeatBet,
    isFromHistory,
    ts: Date.now(),
  },
});

export const basketUpdateItemIsFromMultichoice = ({ oddId, isFromMultichoice }) => ({
  type: BasketActionTypes.UPDATE_ITEM_IS_FROM_MULTICHOICE,
  payload: {
    oddId,
    isFromMultichoice,
  },
});

export const basketOutcomeSelected = ({
  oddId,
  matchId,
  betId,
  sourceElementUuid,
  isFromLineUp = false,
  isFromMultichoice = false,
  isRepeatBet = false,
  isFromHistory = false,
}) => ({
  type: BasketActionTypes.OUTCOME_SELECTED,
  payload: {
    oddId,
    matchId,
    betId,
    sourceElementUuid,
    isFromLineUp,
    isFromMultichoice,
    isRepeatBet,
    isFromHistory,
  },
});

export const basketOutcomeUnselected = ({ oddId, matchId, isFromMultichoice }) => ({
  type: BasketActionTypes.OUTCOME_UNSELECTED,
  payload: {
    oddId,
    matchId,
    isFromMultichoice,
  },
});

type BasketAlterCachedMMAPIResponseInfosParams = {
  result: MMAPIResponseResult;
  outcomes?: Array<{ betId: number; outcomeId: number }>;
  matchId: number;
};
export const basketAlterCachedMMAPIResponseInfos = ({
  result,
  outcomes,
  matchId,
}: BasketAlterCachedMMAPIResponseInfosParams) => ({
  type: BasketActionTypes.ALTER_CACHED_MM_API_RESPONSE_INFOS,
  payload: {
    result,
    outcomes,
    matchId,
  },
});

type BasketRemoveCachedMMAPIResponseInfosParams = {
  matchId: number;
};
export const basketRemoveCachedMMAPIResponseInfos = ({ matchId }: BasketRemoveCachedMMAPIResponseInfosParams) => ({
  type: BasketActionTypes.REMOVE_CACHED_MM_API_RESPONSE_INFOS,
  payload: {
    matchId,
  },
});

/**
 * Create a function to remove an outcome from basket and dispatch an action
 * to update the free bets
 *
 * @param  {number} options.oddId     the outcome id
 * @param  {number} options.matchId   the targeted match id
 * @return {function}                 Return a function
 */
export const basketDelItem = ({ oddId, matchId }) => ({
  type: BasketActionTypes.DEL_ITEM,
  payload: {
    oddId,
    matchId,
  },
});

/**
 * Create an action to remove everything from basket
 *
 * @return {Object} Return an action object
 */
export const basketClear = () => ({
  type: BasketActionTypes.CLEAR,
});

/**
 * Create an action to change the basket's mode
 *
 * @param  {number} options.mode The next mode
 * @return {Object}              Return an action object
 */
export const basketSwitchMode = ({ mode }) => ({
  type: BasketActionTypes.SWITCH_MODE,
  payload: { mode },
});

/**
 * Create an action to request to change the basket's mode
 *
 * @param  {number} options.mode The next mode
 * @return {Object}              Return an action object
 */
export const basketRequestSwitchMode = ({ mode }) => ({
  type: BasketActionTypes.REQUEST_SWITCH_MODE,
  payload: { mode },
});

/**
 * Create an action to set the defaultStake value
 *
 * @param  {Number}                    lastAmout value
 * @return {Object}                    Return an action object
 */
const basketSetDefaultStake = (lastAmount) => (dispatch) => {
  dispatch({
    type: BasketActionTypes.SET_DEFAULT_STAKE,
    payload: { lastAmount },
  });
  dispatch(storageBasketSave);
};

/**
 * Create an action to change the outcome's stake value
 *
 * @param  {number}        options.oddId  The outcome id
 * @param  {string|number} options.stake  The next stake value
 * @param  {?boolean}      options.force  Force update
 * @return {Object}                       Return an action object
 */
export const basketItemStakeChange =
  ({ oddId, stake, force }) =>
  (dispatch) => {
    dispatch({
      type: BasketActionTypes.ITEM_STAKE_CHANGE,
      payload: { oddId, stake, force },
    });
    dispatch(storageBasketSave);
  };

/**
 * Create an action to change the outcome's banker value
 *
 * @param  {number}  options.oddId  The outcome id
 * @param  {boolean} options.banker The banker value
 * @return {Object}                 Return an action object
 */
export const basketItemBankerChange =
  ({ oddId, banker }) =>
  (dispatch) => {
    dispatch({
      type: BasketActionTypes.ITEM_BANKER_CHANGE,
      payload: { oddId, banker },
    });
    dispatch(storageBasketSave);
  };

/**
 * Create an action to change the outcome's status to ok
 *
 * @param  {number} options.oddId The outcome id
 * @return {Object}               Return an action object
 */
const basketSetItemStatusOk = ({ oddId }) => ({
  type: BasketActionTypes.SET_ITEM_STATUS_OK,
  payload: {
    oddId,
    isMobile: Target.__MOBILE__,
  },
});

/**
 * Create an action to validate outcomes
 *
 * @param  {Array}  options.betList           Items to validate
 * @param  {string} options.betslipId         The betslip reference
 * @param  {string} options.additionalMessage An additional message
 * @param  {string} options.traderId          The traderId reference
 * @return {Object}                           Return an action object
 */
const basketValidateItems = ({ betList, betslipId, additionalMessage, traderId }) => ({
  type: BasketActionTypes.VALIDATE_ITEMS,
  payload: {
    betList,
    betslipId,
    additionalMessage,
    traderId,
  },
});

/**
 * Create an action to change the outcome's status to waiting
 *
 * @param  {number} options.oddId             The outcome id
 * @return {Object}                           Return an action object
 */
const basketSetItemStatusWaiting = ({ oddId, traderId }) => ({
  type: BasketActionTypes.SET_ITEM_STATUS_WAITING,
  payload: {
    oddId,
    traderId,
    isMobile: Target.__MOBILE__,
  },
});

/**
 * Create an action to change the outcome's status to error
 *
 * @param  {number} options.oddId     The outcome id
 * @param  {string} options.error     The error message
 * @return {Object}                   Return an action object
 */
const basketSetItemStatusError = ({ oddId, error }) => ({
  type: BasketActionTypes.SET_ITEM_STATUS_ERROR,
  payload: {
    oddId,
    error,
    isMobile: Target.__MOBILE__,
  },
});

/**
 * Create an action to change the basket's status to validated
 *
 * @param  {string}  options.traderId         The betslip reference
 * @return {Object}                            Return an action object
 */
const basketSetStatusWaiting = ({ traderId }) => ({
  type: BasketActionTypes.SET_STATUS_WAITING,
  payload: {
    traderId,
  },
});

/**
 * Create an action to change the basket's status to error
 *
 * @param  {Object} options.error The error object
 * @return {Object}               Return an action object
 */
const basketSetStatusError = ({ error }) => ({
  type: BasketActionTypes.SET_STATUS_ERROR,
  payload: { error },
});

/**
 * Create an action to pass notification info
 *
 * @param  {number} options.traderId  The betslip reference
 * @param  {string} options.error     The error message
 * @return {Object}                   Return an action object
 */
const basketSetNotification = ({ traderId, betslipId, error, additionalMessage }) => ({
  type: BasketActionTypes.SET_NOTIFICATION,
  payload: {
    traderId,
    betslipId,
    error,
    additionalMessage,
    isMobile: Target.__MOBILE__,
  },
});

/**
 * Create an action to change the basket's system selection
 *
 * @param  {string}  options.key      The system key
 * @param  {boolean} options.selected The selected value
 * @return {Object}                   Return an action object
 */
export const basketSystemItemSelectionChange =
  ({ key, selected }) =>
  (dispatch) => {
    dispatch({
      type: BasketActionTypes.SYSTEM_ITEM_SELECTION_CHANGE,
      payload: { key, selected },
    });
    dispatch(storageBasketSave);
  };

/**
 * Create an action to reset the basket's system selection
 *
 * @return {Object}                   Return an action object
 */
export const basketSystemItemSelectionReset = () => (dispatch) => {
  dispatch({
    type: BasketActionTypes.SYSTEM_ITEM_SELECTION_RESET,
  });
  dispatch(storageBasketSave);
};

/**
 * Create an action to refresh the basket
 *
 * @param  {Object} options The options
 * @return {Object}          Return an action object
 */
const basketRefresh = (options) => (dispatch) => {
  dispatch({
    type: BasketActionTypes.REFRESH,
    payload: { force: options && options.force === true },
  });
  dispatch(storageBasketSave);
};

/**
 * Create an action to accept the basket's outcome value changes
 *
 * @return {Object} Return an action object
 */
export const basketAcceptChanges = () => ({
  type: BasketActionTypes.ACCEPT_CHANGES,
});

/**
 * Create an action to change the basket's stake value
 *
 * @param  {string} options.stake The stake value
 * @return {Object}               Return an action object
 */
export const basketStakeChange =
  ({ stake, force }) =>
  (dispatch) => {
    dispatch({
      type: BasketActionTypes.STAKE_CHANGE,
      payload: { stake, force },
    });
    dispatch(storageBasketSave);
  };

/**
 * Create an action to inform that the basket's submit successed
 *
 * @return {Object} Return an action object
 */
const basketSubmitOk = ({ response, body }) => ({
  type: BasketActionTypes.SUBMIT_OK,
  payload: {
    response,
    body,
  },
});

/**
 * Create an action to inform that the basket's submit failed
 *
 * @param  {string} err The error message
 * @return {Object}     Return an action object
 */
const basketSubmitKo = (err) => ({
  type: BasketActionTypes.SUBMIT_KO,
  payload: {
    err,
  },
});

const basketReset = () => (dispatch) => {
  dispatch({ type: BasketActionTypes.RESET });
  dispatch(storageBasketSave);
};

/**
 * FIXME
 * [description]
 * @param  {[type]} options.freebetId [description]
 * @return {[type]}                   [description]
 */
const basketDelFreebet = ({ freebetId }) => ({
  type: BasketActionTypes.DEL_FREEBET,
  payload: { freebetId },
});

const basketDelBonus = ({ promotionId }) => ({
  type: BasketActionTypes.DEL_BONUS,
  payload: { promotionId },
});

/**
 * Handle the submit api's response in MODE SIMPLE
 *
 * @param  {Object} payload The payload
 * @return {undefined}
 */
const basketReceivedHandlerModeSimple = (payload) => {
  const { dispatch, lang, response, body, basket, getState } = payload;

  const { items } = basket;

  /* MODE SIMPLE */
  switch (response.status) {
    case 404:
    case 500: {
      [
        basketReset(),
        basketSetStatusError({
          error: {
            estr: __('Service unavailable'),
          },
        }),
      ].forEach((action) => dispatch(action));
      break;
    }
    default: {
      /**
       * REDIRECT
       */
      const { url, ecode } = body;

      if (url !== undefined) {
        [basketReset(), basketSetStatusError({})].forEach((action) => dispatch(action));
        dispatch(externalLinkOpen(getRedirectUrl(RedirectServices.ENCODED_URL, { url }), { auth: true }));
        return;
      }
      /**
       * HANDLE SUCCESS
       */
      if (ecode === 0) {
        const { bsid: betslipId, b: betList, am: additionalMessage, kl, gr, monopoly } = body;

        if (betslipId !== undefined && betList !== undefined) {
          dispatch(
            basketValidateItems({
              betList,
              betslipId,
              additionalMessage,
            }),
          );

          // r -> reference for betbuilder item
          // bid -> outcome id
          _each(betList, ({ bid, r }) => {
            const finalBid = r || bid;
            if (items[finalBid].selectedFreebetId !== undefined) {
              dispatch(
                basketDelFreebet({
                  freebetId: items[finalBid].selectedFreebetId,
                }),
              );
            }
            if (items[finalBid].selectedBonusId !== undefined) {
              dispatch(
                basketDelBonus({
                  promotionId: items[finalBid].selectedBonusId,
                }),
              );
            }

            if (kl) {
              const { id, type } = kl;
              const collectibleId = id ?? type;

              dispatch(basketItemSetKLEligibleCollectible({ oddsId: finalBid, collectibleId }));
            }

            if (gr) {
              dispatch(basketItemSetIsGRActive({ oddsId: finalBid, isGRActive: true }));
            }

            if (monopoly) {
              const { familyId, cardIndex: index } = monopoly;
              dispatch(basketItemSetMPCard({ oddsId: finalBid, familyId, index }));
            }
          });

          dispatch(basketSetSelection());

          /* FIXME: refresh odds value in case the value changed */
        } else if (Target.__ENV__ !== 'production') {
          const err = `Invalid response from betslip. ${JSON.stringify(body)}`;
          throw new Error(err);
        }
        /**
         * WAITING FOR TRADERS
         */
      } else if (ecode === BetslipErrorCodes.TRADER) {
        const { edetails, trid, kl, gr, monopoly } = body;

        if (edetails !== undefined) {
          _each(edetails, (edetail, oddId) => {
            dispatch(basketSetItemStatusWaiting({ oddId, traderId: trid }));

            if (kl) {
              const { id, type } = kl;
              const collectibleId = id ?? type;

              dispatch(basketItemSetKLEligibleCollectible({ oddsId: oddId, collectibleId }));
            }

            if (gr) {
              dispatch(basketItemSetIsGRActive({ oddsId: oddId, isGRActive: true }));
            }

            if (monopoly) {
              const { familyId, cardIndex: index } = monopoly;
              dispatch(basketItemSetMPCard({ oddsId: oddId, familyId, index }));
            }
          });
        }
        /**
         * HANDLING ERROR CODE
         */
      } else if (ecode === BetslipErrorCodes.KOH_LANTA) {
        const { einfo, edetails } = body;
        const { code } = einfo ?? {};

        const errorMsg = KLErrorLabelsByErrorCode[code];

        if (edetails !== undefined) {
          _each(edetails, (edetail, oddId) => {
            dispatch(
              basketSetItemStatusError({
                oddId,
                error: {
                  ecode: code,
                  estr: errorMsg ?? KLErrorLabelsByErrorCode.default,
                },
              }),
            );
          });
        }
      } else if (ecode === BetslipErrorCodes.GIFT_RACE) {
        const { einfo, edetails } = body;
        const { code } = einfo ?? {};
        const grSkinId = selectGRGlobalSkinId(getState());

        const errorMsg = getGRErrorLabelsByErrorCode(grSkinId)[code];

        if (edetails !== undefined) {
          _each(edetails, (edetail, oddId) => {
            dispatch(
              basketSetItemStatusError({
                oddId,
                error: {
                  ecode: code,
                  estr: errorMsg,
                },
              }),
            );
          });
        }
      } else if (ecode === BetslipErrorCodes.MONOPOLY) {
        const { einfo, edetails } = body;
        const { code } = einfo ?? {};

        const errorMsg = getMPErrorLabelsByErrorCode()[code];

        if (edetails !== undefined) {
          _each(edetails, (edetail, oddId) => {
            dispatch(
              basketSetItemStatusError({
                oddId,
                error: {
                  ecode: code,
                  estr: errorMsg,
                },
              }),
            );
          });
        }
      } else if (ecode === BetslipErrorCodes.BOOM) {
        const { einfo, edetails } = body;
        const { code } = einfo ?? {};
        const boomSkinId = selectBoomGlobalSkinId(getState());

        const errorMsg = getBoomErrorLabelsByErrorCode(boomSkinId)[code];

        if (edetails !== undefined) {
          _each(edetails, (edetail, oddId) => {
            dispatch(
              basketSetItemStatusError({
                oddId,
                error: {
                  ecode: code,
                  estr: errorMsg,
                },
              }),
            );
          });
        }
      } else {
        const { edetails, einfo, estr } = body;
        let errorMessage = null;
        let errorEcode = null;

        if (
          [
            BetslipErrorCodes.ACCESS_DENIED,
            BetslipErrorCodes.BAD_REQUEST,
            BetslipErrorCodes.MAINTENANCE,
            BetslipErrorCodes.DB_ERROR,
          ].indexOf(ecode) !== -1
        ) {
          dispatch(basketReset());
        }

        if (edetails !== undefined) {
          _each(edetails, (edetail, oddId) => {
            /* set basket item stake to einfo.max or einfo.min */
            if (
              [
                BetslipErrorCodes.NOT_ENOUGH_MONEY,
                BetslipErrorCodes.AMOUNT_OVER_LIMIT,
                BetslipErrorCodes.AMOUNT_UNDER_LIMIT,
                BetslipErrorCodes.DAILY_MODERATOR_REACHED,
                BetslipErrorCodes.WEEKLY_MODERATOR_REACHED,
                BetslipErrorCodes.MONTHLY_MODERATOR_REACHED,
              ].indexOf(ecode) !== -1
            ) {
              if (einfo !== undefined) {
                if (einfo.max !== undefined && einfo.max > 0) {
                  dispatch(
                    basketItemStakeChange({
                      stake: parseFloat(einfo.max / 100).toString(),
                      force: true,
                      oddId,
                    }),
                  );
                } else if (einfo.min !== undefined && einfo.min > 0) {
                  dispatch(
                    basketItemStakeChange({
                      stake: parseFloat(einfo.min / 100).toString(),
                      force: true,
                      oddId,
                    }),
                  );
                }
              }
            }

            /* set error message and ecode */
            if (
              [
                BetslipErrorCodes.ACCESS_DENIED,
                BetslipErrorCodes.BAD_REQUEST,
                BetslipErrorCodes.MAINTENANCE,
                BetslipErrorCodes.DB_ERROR,
                BetslipErrorCodes.ODDS_CHANGED,
                BetslipErrorCodes.INCOMPATIBLE_BETS,
                BetslipErrorCodes.INCOMPATIBLE_OUTRIGHT,
              ].indexOf(ecode) === -1
            ) {
              errorEcode = ecode;
              errorMessage = BasketUtils.getErrorCodeMessage({
                ecode,
                einfo,
                estr,
                lang,
              });
            }

            /* set error message and ecode and adjust odd value in some cases */
            if (
              [
                BetslipErrorCodes.BETSLIP_DETAIL_INCOMPATIBLE_BETS,
                BetslipErrorCodes.BETSLIP_DETAIL_INCOMPATIBLE_OUTRIGHT,
                BetslipErrorCodes.BETSLIP_DETAIL_BET_SUSPENDED,
                BetslipErrorCodes.BETSLIP_DETAIL_BET_STOPPED,
                BetslipErrorCodes.BETSLIP_DETAIL_ODDS_CHANGED_INCREASE,
                BetslipErrorCodes.BETSLIP_DETAIL_ODDS_CHANGED_DECREASE,
                // MyMatch
                BetslipErrorCodes.MYMATCH_UNAVAILABLE_500,
                BetslipErrorCodes.MYMATCH_UNAVAILABLE_1,
                BetslipErrorCodes.MYMATCH_INCOHERENT_SELECTIONS,
                BetslipErrorCodes.MYMATCH_TOO_MANY_SELECTIONS,
                BetslipErrorCodes.MYMATCH_SELECTION_UNAVAILABLE,
              ].indexOf(edetail.ecode) !== -1
            ) {
              errorEcode = edetail.ecode;
              errorMessage = BasketUtils.getErrorCodeMessage({
                ecode: edetail.ecode,
                einfo: edetail.einfo,
                estr: edetail.estr,
                lang,
              });
              if (edetail.einfo !== undefined) {
                if (edetail.einfo.o !== undefined) {
                  dispatch(
                    receiveEntities({
                      odds: {
                        [oddId]: parseFloat(edetail.einfo.o / 1000),
                      },
                    }),
                  );
                  dispatch(basketRefresh({ force: true }));
                }
              }
            }
            if (errorEcode && errorMessage) {
              dispatch(
                basketSetItemStatusError({
                  oddId,
                  error: {
                    ecode: errorEcode,
                    estr: errorMessage,
                  },
                }),
              );
            }
            /* TODO: freebets */
          });
        }
        /* set global error message in some cases */
        if (
          [
            BetslipErrorCodes.ACCESS_DENIED,
            BetslipErrorCodes.BAD_REQUEST,
            BetslipErrorCodes.MAINTENANCE,
            BetslipErrorCodes.DB_ERROR,
            BetslipErrorCodes.ODDS_CHANGED,
          ].indexOf(ecode) !== -1
        ) {
          dispatch(
            basketSetStatusError({
              error: {
                ecode,
                estr: BasketUtils.getErrorCodeMessage({
                  ecode,
                  einfo,
                  estr,
                  lang,
                }),
              },
            }),
          );
        }
      }
    }
  }
  dispatch(storageBasketSave);
};

/**
 * Handle the submit api's response in MODE COMBINED and MODE SYSTEM
 *
 * @param  {Object} payload The payload
 * @return {Object}         Return an action object
 */
const basketReceivedHandlerModeMulti = (payload) => {
  const { dispatch, lang, response, body, basket, getState } = payload;

  const { selectedFreebetId, selectedBonusId, mode } = basket;
  /* MODE COMBINED - MODE SYSTEM */
  switch (response.status) {
    case 404:
    case 500: {
      [
        basketReset(),
        basketSetStatusError({
          error: {
            estr: __('Service unavailable'),
          },
        }),
      ].forEach((action) => dispatch(action));
      break;
    }
    default: {
      /*
       *
       *
          REDIRECT
       *
       *
      */
      const { url } = body;
      if (url !== undefined) {
        [basketReset(), basketSetStatusError({})].forEach((action) => dispatch(action));
        dispatch(externalLinkOpen(getRedirectUrl(RedirectServices.ENCODED_URL, { url }), { auth: true }));
        return;
      }

      const { ecode } = body;
      /**
       *
       * SUCCESS
       *
       */
      if (ecode === 0) {
        const { bsid: betslipId, am: additionalMessage, b: betList, kl, gr, monopoly } = body;

        if (betslipId !== undefined) {
          dispatch(
            basketValidateItems({
              betList,
              betslipId,
              additionalMessage,
            }),
          );
          dispatch(basketSetSelection());
          if (mode === BasketModes.COMBINED) {
            if (selectedFreebetId !== undefined) {
              dispatch(basketDelFreebet({ freebetId: selectedFreebetId }));
            }

            if (selectedBonusId !== undefined) {
              dispatch(basketDelBonus({ promotionId: selectedBonusId }));
            }

            if (kl) {
              const { id, type } = kl;
              const collectibleId = id ?? type;

              dispatch(basketSetKLEligibleCollectible({ collectibleId }));
            }

            if (gr) {
              dispatch(basketSetIsGRActive({ isGRActive: true }));
            }

            if (monopoly) {
              const { familyId, cardIndex: index } = monopoly;
              dispatch(basketSetMPCard({ familyId, index }));
            }
          }
        } else if (Target.__ENV__ !== 'production') {
          const err = `Invalid response from betslip. ${JSON.stringify(body)}`;
          throw new Error(err);
        }
        /**
         * WAITING FOR TRADERS
         */
      } else if (ecode === BetslipErrorCodes.TRADER) {
        const { trid, kl, gr, monopoly } = body;

        if (kl) {
          const { id, type } = kl;
          const collectibleId = id ?? type;

          dispatch(basketSetKLEligibleCollectible({ collectibleId }));
        }

        if (gr) {
          dispatch(basketSetIsGRActive({ isGRActive: true }));
        }

        if (monopoly) {
          const { familyId, cardIndex: index } = monopoly;
          dispatch(basketSetMPCard({ familyId, index }));
        }

        dispatch(basketSetStatusWaiting({ traderId: trid }));
        /**
         *
         *  ERROR
         *
         */
      } else if (ecode === BetslipErrorCodes.KOH_LANTA) {
        const { einfo } = body;
        const { code } = einfo ?? {};

        const errorMsg = KLErrorLabelsByErrorCode[code];

        dispatch(
          basketSetStatusError({
            error: {
              code,
              estr: errorMsg,
            },
          }),
        );
      } else if (ecode === BetslipErrorCodes.GIFT_RACE) {
        const { einfo } = body;
        const { code } = einfo ?? {};
        const grSkinId = selectGRGlobalSkinId(getState());

        const errorMsg = getGRErrorLabelsByErrorCode(grSkinId)[code];

        dispatch(
          basketSetStatusError({
            error: {
              code,
              estr: errorMsg,
            },
          }),
        );
      } else if (ecode === BetslipErrorCodes.MONOPOLY) {
        const { einfo } = body;
        const { code } = einfo ?? {};

        const errorMsg = getMPErrorLabelsByErrorCode()[code];

        dispatch(
          basketSetStatusError({
            error: {
              code,
              estr: errorMsg,
            },
          }),
        );
      } else if (ecode === BetslipErrorCodes.BOOM) {
        const { einfo } = body;
        const { code } = einfo ?? {};
        const boomSkinId = selectBoomGlobalSkinId(getState());

        const errorMsg = getBoomErrorLabelsByErrorCode(boomSkinId)[code];

        dispatch(
          basketSetStatusError({
            error: {
              code,
              estr: errorMsg,
            },
          }),
        );
      } else {
        const { edetails, einfo, estr } = body;

        if (
          [
            BetslipErrorCodes.NOT_ENOUGH_MONEY,
            BetslipErrorCodes.AMOUNT_OVER_LIMIT,
            BetslipErrorCodes.AMOUNT_UNDER_LIMIT,
            BetslipErrorCodes.DAILY_MODERATOR_REACHED,
            BetslipErrorCodes.WEEKLY_MODERATOR_REACHED,
            BetslipErrorCodes.MONTHLY_MODERATOR_REACHED,
          ].indexOf(ecode) !== -1
        ) {
          if (einfo !== undefined) {
            if (einfo.max !== undefined && einfo.max > 0) {
              dispatch(
                basketStakeChange({
                  stake: parseFloat(einfo.max / 100).toString(),
                  force: true,
                }),
              );
            } else if (einfo.min !== undefined && einfo.min > 0) {
              dispatch(
                basketStakeChange({
                  stake: parseFloat(einfo.min / 100).toString(),
                  force: true,
                }),
              );
            }
          }
        }

        if (edetails !== undefined) {
          _each(edetails, (edetail, oddId) => {
            let errorEcode = null;
            let errorMessage = null;
            /* set error message and ecode and adjust odd value in some cases */
            if (
              [
                BetslipErrorCodes.BETSLIP_DETAIL_INCOMPATIBLE_BETS,
                BetslipErrorCodes.BETSLIP_DETAIL_INCOMPATIBLE_OUTRIGHT,
                BetslipErrorCodes.BETSLIP_DETAIL_BET_SUSPENDED,
                BetslipErrorCodes.BETSLIP_DETAIL_BET_STOPPED,
                BetslipErrorCodes.BETSLIP_DETAIL_ODDS_CHANGED_INCREASE,
                BetslipErrorCodes.BETSLIP_DETAIL_ODDS_CHANGED_DECREASE,
                // MyMatch
                BetslipErrorCodes.MYMATCH_UNAVAILABLE_500,
                BetslipErrorCodes.MYMATCH_UNAVAILABLE_1,
                BetslipErrorCodes.MYMATCH_INCOHERENT_SELECTIONS,
                BetslipErrorCodes.MYMATCH_TOO_MANY_SELECTIONS,
                BetslipErrorCodes.MYMATCH_SELECTION_UNAVAILABLE,
              ].indexOf(edetail.ecode) !== -1
            ) {
              errorEcode = edetail.ecode;
              errorMessage = BasketUtils.getErrorCodeMessage({
                ecode: edetail.ecode,
                einfo: edetail.einfo,
                estr: edetail.estr,
                lang,
              });
              if (edetail.einfo !== undefined) {
                if (edetail.einfo.o !== undefined) {
                  dispatch(
                    receiveEntities({
                      odds: {
                        [oddId]: parseFloat(edetail.einfo.o / 1000),
                      },
                    }),
                  );
                  dispatch(basketRefresh({ force: true }));
                }
              }
            }
            if (errorEcode && errorMessage) {
              dispatch(
                basketSetItemStatusError({
                  oddId,
                  error: {
                    ecode: errorEcode,
                    estr: errorMessage,
                  },
                }),
              );
            } else {
              dispatch(basketSetItemStatusOk({ oddId }));
            }
            /* TODO: freebets */
          });
        }

        /* set global error message in some cases */
        if (
          [
            BetslipErrorCodes.INCOMPATIBLE_BETS,
            BetslipErrorCodes.INCOMPATIBLE_OUTRIGHT,
            BetslipErrorCodes.MYMATCH_PROBLEM,
          ].indexOf(ecode) === -1
        ) {
          dispatch(
            basketSetStatusError({
              error: {
                ecode,
                estr: BasketUtils.getErrorCodeMessage({
                  ecode,
                  einfo,
                  estr,
                  lang,
                }),
              },
            }),
          );
        }
      }
    }
  }
  dispatch(storageBasketSave);
};

/**
 * Redirect the submit api's response to the right function
 *
 * @param  {Object}    options.basket      The basket
 * @param  {...Object} options.otherParams The rest of params
 * @return {undefined}
 */
const basketReceivedHandler = ({ basket, body, ...otherParams }) => {
  const { mode } = basket;
  switch (mode) {
    case BasketModes.SIMPLE:
      basketReceivedHandlerModeSimple({ basket, body, ...otherParams });
      break;
    case BasketModes.COMBINED:
    case BasketModes.SYSTEM:
      basketReceivedHandlerModeMulti({ basket, body, ...otherParams });
      break;
    default:
      break;
  }
};

/**
 * Create an action to memorize items from basket
 *
 * @param  {Object}    options.basket      The basket
 * @return {undefined}
 */
const basketSetSelection = () => (dispatch, getState) => {
  const { basket } = getState();
  if (_size(basket.items) > 0) {
    dispatch({ type: BasketActionTypes.SET_SELECTION });
  }
};

export const basketGetSelection = () => (dispatch, getState) => {
  const { basket } = getState();
  if (_size(basket.itemsMemoizedForSelection) > 0) {
    dispatch({ type: BasketActionTypes.GET_SELECTION });
    dispatch(basketCheckFreebets());
    dispatch(resetKLEligibleCollectibles());
    dispatch(resetAllGRActives());
    dispatch(resetAllMPActives());
    dispatch(basketResetBoomActives());
  }
};

/**
 * Create a function that dispatch some async actions related to the
 * basket's submit
 *
 * @return {function}     The submit function
 */
export const basketSubmit = () => (dispatch, getState) => {
  dispatch(basketClearValidatedAndWaiting);

  const state = getState();
  const { basket, user } = state;
  const { token: jwt } = user?.jwt ?? {};
  const { sessionId, cc } = user;
  const { status } = basket;
  const lang = getLang();

  if (status !== BasketStatuses.PENDING) {
    dispatch({ type: BasketActionTypes.SUBMIT });
    const data = BasketUtils.createBetslipRequestPayload(state);

    if (data.length > 0) {
      _each(data, (bet) => {
        // A correspond au montant de mise
        // B correspond au éventualité sous la forme
        //  [{ BID: <id>, O: <value>, BK?: <banker>}, ...]
        //  BID -> odd identifier,
        //  O -> odd value,
        //  BK -> odd banker
        // T correspond au type de pari
        // 1: SIMPLE
        // 2: COMBINE
        // 3: SYSTEM
        const { A, B, T } = bet;

        if (A > 0 && B.length > 0) {
          const form = `bsm=${JSON.stringify(bet)}`;
          const url = basketUrl[lang];
          const method = 'post';
          const credentials = 'same-origin';
          const options = {
            url,
            method,
            credentials,
            body: form,
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
              ...getCustomHeaders({ jwt, sessionId, cc, tracker: Client.tracker }),
            },
          };
          const hooks = {
            received: (response, body) => {
              dispatch(basketSubmitOk({ response, body }));
              basketReceivedHandler({
                dispatch,
                lang,
                basket,
                response,
                body,
                getState,
              });
            },
            failure: (err, extra) => {
              dispatch(
                sendReport({
                  message: 'validate_betslip failure',
                  error: err.toString(),
                  extra,
                }),
              );

              Sentry.captureMessage('User sign-out from validate_betslip failure');
              [
                basketSubmitKo(err.toString()),
                basketReset(),
                basketSetStatusError({
                  error: {
                    estr: __('Service unavailable'),
                  },
                }),
              ].forEach((action) => dispatch(action));
            },
          };
          dispatch(makeApiCall({ options, hooks }));
        } else if (T === 1) {
          // NOTE: ce test permet d'eviter un cas d'erreur (mise à 0 en mode simple)

          // Si c'est un pari simple, on repasse son status a OK pour ne pas
          // le laisser dans un état PENDING et bloquer le panier
          // dispatch(basketSetItemStatusOk({ oddId: B[0].BID }));

          const errorMessage = {
            ecode: BetslipErrorCodes.AMOUNT,
          };
          const err = {
            ...errorMessage,
            estr: BasketUtils.getErrorCodeMessage(errorMessage),
          };
          dispatch(
            basketSetItemStatusError({
              oddId: B[0].BID,
              error: err,
            }),
          );
        } else {
          dispatch(basketReset());
        }
      });
    } else {
      dispatch(basketReset());
    }
  }
};

/**
 * Create a function that dispatch freebet's data to the store
 *
 * [description]
 * @param  {Array}      freebets list
 * @return {undefined}
 */
const basketReceiveFreebets = (data) => (dispatch) => {
  dispatch({ type: BasketActionTypes.RECEIVE_FREEBETS, payload: { data } });
  dispatch(basketCheckFreebets());
};

/**
 * Create a function that dispatch booster's data to the store
 *
 * [description]
 * @param  {Array}      boosters list
 * @return {undefined}
 */
const basketReceiveBoosters = (data) => (dispatch) => {
  dispatch({ type: BasketActionTypes.RECEIVE_BOOSTERS, payload: { data } });
};

const basketReceiveBonuses = (data) => (dispatch) => {
  dispatch({ type: BasketActionTypes.RECEIVE_BONUSES, payload: { data } });
};

const basketSetLoadingOk = () => (dispatch) => {
  dispatch({ type: BasketActionTypes.SET_LOADING_OK });
  dispatch(basketCheckFreebets());
  dispatch(storageBasketSave);
};

export const basketClearValidatedAndWaiting = () => (dispatch) => {
  dispatch({
    type: BasketActionTypes.CLEAR_VALIDATED_AND_WAITING,
  });
};

const basketSetCashout = (payload) => ({
  type: BasketActionTypes.SET_CASHOUT,
  payload,
});

export const basketSetKeyboard = (isKeyboardVisible) => ({
  type: BasketActionTypes.SET_KEYBOARD,
  payload: { isKeyboardVisible },
});

/**
 * Set the outcome's selected free bet boolean
 *
 * @param  {number} options.oddId     The outcome id
 * @param  {number} options.freebetId The free bet id
 * @return {function}                 Return a function
 */
export const basketItemSetFreebetBoolean =
  ({ oddId, freebetId }) =>
  (dispatch, getState) => {
    const { basket } = getState();

    dispatch({
      type: BasketActionTypes.SET_ITEM_FREEBET_BOOLEAN,
      payload: {
        oddId,
        freebetId,
      },
    });

    if (basket?.eligibleFreebets) {
      dispatch(basketCheckEligibleFreebets());
    }
  };

export const basketSetFreebetBoolean =
  ({ freebetId }) =>
  (dispatch) => {
    dispatch({
      type: BasketActionTypes.SET_FREEBET_BOOLEAN,
      payload: { freebetId },
    });
  };

export const basketUpdateItemValue = ({ oddId, value }) => ({
  type: BasketActionTypes.UPDATE_ITEM_VALUE,
  payload: {
    oddId,
    value,
  },
});

export const basketImportItems = ({
  items,
  isRepeatBet = false,
  isFromHistory = false,
  betslipReference,
  sourceElementUuid,
}) => ({
  type: BasketActionTypes.IMPORT_ITEMS,
  payload: {
    items,
    isRepeatBet,
    isFromHistory,
    sourceElementUuid,
    betslipReference,
  },
});

// eslint-disable-next-line import/no-anonymous-default-export
export default {
  basketSetLoadingOk,
  basketAddItem,
  basketAddItems,
  basketDelItem,
  basketClear,
  basketSwitchMode,
  basketItemStakeChange,
  basketItemBankerChange,
  basketSystemItemSelectionChange,
  basketSystemItemSelectionReset,
  basketRefresh,
  basketSetDefaultStake,
  basketStakeChange,
  basketSubmit,
  basketSetSelection,
  basketGetSelection,
  basketReset,
  basketClearValidatedAndWaiting,
  basketSetStatusError,
  basketSetNotification,
  basketItemSelectedFreebetChange,
  basketItemSelectedBonusChange,
  basketAcceptChanges,
  basketSetFreebets,
  basketSetBonuses,
  basketSetItemFreebets,
  basketSetItemBonuses,
  basketSetItemStatusError,
  basketCheckFreebets,
  basketSelectedFreebetChange,
  basketSelectedBonusChange,
  basketReceiveFreebets,
  basketReceiveBoosters,
  basketReceiveBonuses,
  basketDelFreebet,
  basketSetBooster,
  basketSetCashout,
  basketValidateItems,
  basketSetKeyboard,
  basketItemSetFreebetBoolean,
  basketSetFreebetBoolean,
  basketSanitize,
  basketImportItems,
};
